import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormHelper } from '@common/form-helper';
import { Month } from '@common/month';
import { CardComponent } from '@components/card/card.component';
import { HeightWeightInputComponent } from '@components/consultation-request/height-weight-input/height-weight-input.component';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { FormService } from '@services/form.service';
import { Subscription } from 'rxjs';

import { dateValidator } from '../../../validators/date-validator';

@Component({
  selector: 'app-consultation-request-personal-information',
  templateUrl: './personal-information.component.html',
  styleUrls: ['./personal-information.component.scss'],
  imports: [CardComponent, FormsModule, ReactiveFormsModule, NgIf, NgClass, NgFor, HeightWeightInputComponent],
})
export class PersonalInformationComponent extends FormHelper implements OnInit, OnDestroy {
  @Input() public calendarOptions: { months: Month[]; days: number[]; years: number[] };

  public personalInfoForm: FormGroup;
  public dob: Date | null = null;

  private dobSubscription?: Subscription;
  private formService = inject(FormService);
  private consultationRequestService = inject(ConsultationRequestService);

  /**
   * Initializes the component.
   */
  public ngOnInit(): void {
    this.personalInfoForm = this.formService.consultationRequest.get('personal') as FormGroup;
    this.setDynamicPatientInfoFormValidators();
    if (this.consultationRequestService.consultationOrderDetail) {
      this.prefillPersonalInformation();
    }

    this.subscribeToDobChanges();
  }

  /**
   * Unsubscribe on component destroy.
   */
  public ngOnDestroy(): void {
    this.dobSubscription?.unsubscribe();
  }

  /**
   * Determines if a form control is invalid.
   *
   * @param {string} controlName The name of the form control to check for validity.
   */
  public invalidControl(controlName: string): boolean {
    return this.isInvalid(this.personalInfoForm.get(controlName));
  }

  /**
   * Retrieves the error message for an invalid control.
   *
   * @param {string} controlName the name of the control in the form group
   * @param {string} displayName the display name of the control
   *
   * @returns {string} the error message for the invalid control
   */
  public getInvalidControlErrorMessage(controlName: string, displayName: string): string {
    return this.getControlValidationErrorMessage(this.personalInfoForm.get(controlName), displayName);
  }

  /**
   * Determines if the birthdate is filled out.
   */
  public birthdateFilled(): boolean {
    return ['month', 'day', 'year'].every((controlName: string) => {
      return !this.invalidControl(`birthday.${controlName}`);
    });
  }

  /**
   * Subscription to sync data of birth variable with the birthday form.
   */
  private subscribeToDobChanges(): void {
    this.dob = this.getDob(this.personalInfoForm);
    this.dobSubscription = this.personalInfoForm
      .get('birthday')
      ?.valueChanges.subscribe(() => (this.dob = this.getDob(this.personalInfoForm)));
  }

  /**
   * Sets up dynamic validators.
   */
  private setDynamicPatientInfoFormValidators(): void {
    const personalInfoForm = this.personalInfoForm;
    const birthDayControl = personalInfoForm.get('birthday');
    birthDayControl.setValidators(dateValidator());
  }

  /**
   * Fill form personal information form fields
   */
  private prefillPersonalInformation() {
    const dob = this.consultationRequestService.consultationOrderDetail.date_of_birth;
    this.personalInfoForm.patchValue({
      firstName: this.consultationRequestService.consultationOrderDetail.first_name,
      lastName: this.consultationRequestService.consultationOrderDetail.last_name,
      birthday: {
        month: dob.getMonth() + 1,
        day: dob.getDate(),
        year: dob.getFullYear(),
      },
    });
  }
}
